import { forwardRef, type Ref, type TextareaHTMLAttributes } from 'react';

import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import type { TextAreaState } from './types';

export interface TextAreaProps
  extends ComponentProps<TextareaHTMLAttributes<HTMLTextAreaElement>> {
  state?: keyof typeof TextAreaState;
}

const textarea = variants({
  base: cn(
    'transition-colors-with-outline duration-300 ease-out',
    'outline outline-1 outline-emphasis-subtle',
    'text-body-sm',
    'rounded-xs',
    'p-md',
    'bg-emphasis-default focus:bg-emphasis-strong enabled:hover:bg-emphasis-strong',
    'text-default',
    'placeholder:text-subtle',
    'resize-none',
    'disabled:cursor-not-allowed',
    'disabled:text-disabled',
    'read-only:cursor-not-allowed',
    'read-only:text-disabled',
    'leading-5',
  ),
  variants: {
    state: {
      default: cn(
        'focus:outline-secondary-pressed',
        'enabled:hover:focus:outline-secondary-pressed',
        'enabled:hover:outline-emphasis-stronger',
        'disabled:outline-transparent',
      ),
      error: cn('outline-error-default'),
      warning: cn('outline-warning-default'),
      success: cn('outline-success-default'),
    },
  },
});

export const TextArea = forwardRef(
  (
    { state = 'default', ...props }: TextAreaProps,
    ref: Ref<HTMLTextAreaElement>,
  ) => {
    const stateClasses = textarea({ state });

    const className = getClassNameOverride(props, 'TextArea');

    return (
      <textarea
        ref={ref}
        rows={5}
        className={cn(stateClasses, className)}
        aria-invalid={state === 'error'}
        {...props}
      />
    );
  },
);

TextArea.displayName = 'TextArea';
